import { formatArrayField } from '@client/utils/helpers';
import produitNonCompatibleIcon from '../../img/produit-non-compatible.svg';
import pHIcon from '../../img/pH.svg';
import cloudIcon from '../../img/Cloud.svg';
import developementsIcon from '../../img/developements.svg';
import remanenceIcon from '../../img/Remanence.svg';

const isEmptyHtml = (html) => {
  if (!html) return true;
  const textContent = html.replace(/<[^>]*>/g, '').trim();
  return textContent === '';
};

/**
 * Format a numeric value for French locale display.
 * - Comma as decimal separator (13.6 → 13,6)
 * - Regular non-breaking space as thousands separator (1000 → 1 000)
 *
 * Note: toLocaleString('fr-FR') uses U+202F (narrow no-break space) which
 * renders very thin. We replace it with U+00A0 (regular non-breaking space)
 * for better visibility.
 */
const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return num.toLocaleString('fr-FR').replace(/\u202F/g, '\u00A0');
};

/**
 * Display the sign as saved in admin — strict inequalities stay strict.
 * `<` and `>` are NOT converted to `≤`/`≥` because they don't mean the same
 * thing (strict vs non-strict). Only the multi-character forms `<=` and `>=`
 * are normalized to the single Unicode characters for nicer display.
 */
const formatSign = (sign) => {
  if (!sign) return '';
  const signMap = { '<=': '≤', '>=': '≥' };
  return signMap[sign] || sign;
};

/**
 * Format a sign + numeric value pair for display.
 * Combines sign mapping and French number formatting.
 */
const formatSignValue = (sign, value) => {
  if (value === null || value === undefined) return '';
  const displaySign = formatSign(sign?.sign || sign || '');
  const displayValue = formatNumber(value);
  return `${displaySign} ${displayValue}`.trim();
};

export const mapToxicityData = (healthData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const toxicityData = [];

  // DL50 orale (oral LD50)
  {
    const masked = !healthData?.is_oral_dl50;
    const oralData = {
      criteria: _t('mapdata.dl50_orale', 'DL<sub>50</sub> orale (mg/kg) (rat)'),
      value: masked ? '' : (healthData.oral_dl50_value != null
        ? formatSignValue(healthData.oral_dl50_sign, healthData.oral_dl50_value)
        : ''),
      references: [],
    };

    if (masked) {
      oralData.references.push({ text: '' });
    } else if (healthData.oral_dl50_reference) {
      oralData.references.push({
        source: 'Référence',
        text: healthData.oral_dl50_reference,
      });
    }
    toxicityData.push(oralData);
  }

  // DL50 cutanée (cutaneous LD50)
  {
    const masked = !healthData?.is_cutaneous_dl50;
    const cutaneousData = {
      criteria: `${_t('mapdata.dl50_cutanee', 'DL<sub>50</sub> cutanée (mg/kg)')} (${healthData.cutaneous_dl50_species?.name || ''})`,
      value: masked ? '' : (healthData.cutaneous_dl50_value != null
        ? formatSignValue(healthData.cutaneous_dl50_sign, healthData.cutaneous_dl50_value)
        : ''),
      references: [],
    };

    if (masked) {
      cutaneousData.references.push({ text: '' });
    } else if (healthData.cutaneous_dl50_reference) {
      cutaneousData.references.push({
        source: 'Référence',
        text: healthData.cutaneous_dl50_reference,
      });
    }

    toxicityData.push(cutaneousData);
  }

  // CL50 inhalation (inhalation LC50)
  {
    const masked = !healthData?.is_inhalation_cl50;
    const inhalationData = {
      criteria: _t('mapdata.cl50_inhalation', 'CL<sub>50</sub> inhalation (mg/L) (rat)'),
      value: masked ? '' : (healthData.inhalation_cl50_value != null
        ? formatSignValue(healthData.inhalation_cl50_sign, healthData.inhalation_cl50_value)
        : ''),
      references: [],
    };

    if (masked) {
      inhalationData.references.push({ text: '' });
    } else if (healthData.inhalation_cl50_reference) {
      inhalationData.references.push({
        source: 'Référence',
        text: healthData.inhalation_cl50_reference,
      });
    }

    toxicityData.push(inhalationData);
  }

  // Irritation cutanée (skin irritation)
  {
    const masked = !healthData?.is_skin_irritation;
    const skinIrritationData = {
      criteria: _t('mapdata.irritation_cutanee', 'Irritation cutanée'),
      value: masked ? '' : (healthData.skin_irritation?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      skinIrritationData.references.push({ text: '' });
    } else if (healthData.skin_irritation_reference) {
      skinIrritationData.references.push({
        source: 'Référence',
        text: healthData.skin_irritation_reference,
      });
    }

    toxicityData.push(skinIrritationData);
  }

  // Irritation oculaire (eye irritation)
  {
    const masked = !healthData?.is_eye_irritation;
    const eyeIrritationData = {
      criteria: _t('mapdata.irritation_oculaire', 'Irritation oculaire'),
      value: masked ? '' : (healthData.eye_irritation?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      eyeIrritationData.references.push({ text: '' });
    } else if (healthData.eye_irritation_reference) {
      eyeIrritationData.references.push({
        source: 'Référence',
        text: healthData.eye_irritation_reference,
      });
    }

    toxicityData.push(eyeIrritationData);
  }

  // Sensibilisation cutanée (skin sensitization)
  {
    const masked = !healthData?.is_skin_sensitization;
    const skinSensitizationData = {
      criteria: _t('mapdata.sensibilisation_cutanee', 'Sensibilisation cutanée'),
      value: masked ? '' : (healthData.skin_sensitization?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      skinSensitizationData.references.push({ text: '' });
    } else if (healthData.skin_sensitization_reference) {
      skinSensitizationData.references.push({
        source: 'Référence',
        text: healthData.skin_sensitization_reference,
      });
    }

    toxicityData.push(skinSensitizationData);
  }

  return toxicityData;
};

export const mapChronicData = (healthData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const toxicityData = [];

  {
    const masked = !healthData?.is_carcinogenic;
    const oralData = {
      criteria: _t('mapdata.cancerogenicite', 'Cancérogénicité'),
      value: masked ? '' : (healthData.carcinogenic?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      oralData.references.push({ text: '' });
    } else if (healthData.carcinogenic_reference) {
      oralData.references.push({
        source: 'Référence',
        text: healthData.carcinogenic_reference,
      });
    }
    toxicityData.push(oralData);
  }

  {
    const masked = !healthData?.is_genotoxic;
    const cutaneousData = {
      criteria: _t('mapdata.genotoxicite', 'Génotoxicité'),
      value: masked ? '' : (healthData.genotoxic?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      cutaneousData.references.push({ text: '' });
    } else if (healthData.genotoxic_reference) {
      cutaneousData.references.push({
        source: 'Référence',
        text: healthData.genotoxic_reference,
      });
    }

    toxicityData.push(cutaneousData);
  }

  {
    // R49 — align the web with the PDF (health.html:247-259): "Types d'effets
    // observés sur la génotoxicité" belongs right after Génotoxicité, not after
    // Neurotoxicité where it ends up orphaned from its parent line.
    const genotoxEffectsData = {
      criteria: _t('mapdata.types_effets_genotoxicite', "Types d'effets observés sur la génotoxicité"),
      value: healthData?.type_of_effects_observed_on_genotoxicity || '',
    };
    toxicityData.push(genotoxEffectsData);
  }

  {
    const inhalationData = {
      criteria: _t('mapdata.neurotoxicite', 'Neurotoxicité'),
      value: healthData?.neurotoxic || '',
      references: [],
    };

    if (healthData?.neurotoxic_reference) {
      inhalationData.references.push({
        source: 'Référence',
        text: healthData.neurotoxic_reference,
      });
    }

    toxicityData.push(inhalationData);
  }

  {
    const masked = !healthData?.is_endocrine_disruptor;
    const skinIrritationData = {
      criteria: _t('mapdata.perturbateur_endocrinien', 'Perturbateur endocrinien'),
      value: masked ? '' : (healthData.endocrine_disruptor?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      skinIrritationData.references.push({ text: '' });
    } else if (healthData.endocrine_disruptor_reference) {
      skinIrritationData.references.push({
        source: 'Référence',
        text: healthData.endocrine_disruptor_reference,
      });
    }

    toxicityData.push(skinIrritationData);
  }

  {
    const skinIrritationData = {
      criteria: _t('mapdata.types_effets_endocrinien', "Types d'effets observés sur le système endocrinien"),
      value: healthData?.type_of_effects_observed_on_the_endocrine_system || '',
    };
    toxicityData.push(skinIrritationData);
  }

  {
    const masked = !healthData?.is_reproductive_effects;
    const eyeIrritationData = {
      criteria: _t('mapdata.effets_reproduction', 'Effets sur la reproduction'),
      value: masked ? '' : (healthData.reproductive_effects?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      eyeIrritationData.references.push({ text: '' });
    } else if (healthData.reproductive_effects_reference) {
      eyeIrritationData.references.push({
        source: 'Référence',
        text: healthData.reproductive_effects_reference,
      });
    }

    toxicityData.push(eyeIrritationData);
  }

  {
    const eyeIrritationData = {
      criteria: _t('mapdata.types_effets_reproduction', "Types d'effets observés sur la reproduction"),
      value: healthData?.type_of_effects_observed_on_reproduction || '',
    };
    toxicityData.push(eyeIrritationData);
  }

  {
    const masked = !healthData?.is_developmental_effects;
    const skinSensitizationData = {
      criteria: _t('mapdata.effets_developpement', 'Effets sur le développement'),
      value: masked ? '' : (healthData.developmental_effects?.effect_severity || ''),
      references: [],
    };

    if (masked) {
      skinSensitizationData.references.push({ text: '' });
    } else if (healthData.developmental_effects_reference) {
      skinSensitizationData.references.push({
        source: 'Référence',
        text: healthData.developmental_effects_reference,
      });
    }

    toxicityData.push(skinSensitizationData);
  }

  {
    const skinSensitizationData = {
      criteria: _t('mapdata.types_effets_developpement', "Types d'effets observés sur le développement"),
      value: healthData?.type_of_effects_observed_on_development || '',
    };
    toxicityData.push(skinSensitizationData);
  }

  return toxicityData;
};

export const enviromentEcotoxicologyData = (environmentData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const ecotoxicologyData = [];

  if (environmentData?.dl50_birds_comment) {
    const comment = {
      criteria: _t('mapdata.commentaires_especes_aviaires', 'Commentaires sur les espèces aviaires'),
      value: environmentData?.dl50_birds_comment || '',
      references: [],
    };
    if (environmentData.dl50_birds_comment_reference) {
      comment.references.push({
        source: 'Référence',
        text: environmentData.dl50_birds_comment_reference,
      });
    }
    ecotoxicologyData.push(comment);
  }

  {
    const masked = !environmentData?.is_dl50_birds;
    const speciesName = environmentData?.species_of_bird?.name;
    const bird = {
      criteria: speciesName
        ? `${speciesName} ${_t('mapdata.oiseaux_dl50_suffix', 'DL<sub>50</sub> (mg/kg p.c.)')}`
        : _t('mapdata.oiseaux_dl50_generic', 'Espèce DL<sub>50</sub> (mg/kg p.c.)'),
      value: masked ? '' : (environmentData.dl50_birds_value != null
        ? formatSignValue(environmentData.dl50_birds_sign, environmentData.dl50_birds_value)
        : ''),
      references: [],
    };

    if (masked) {
      bird.references.push({ text: '' });
    } else if (environmentData.dl50_birds_reference) {
      bird.references.push({
        source: 'Référence',
        text: environmentData.dl50_birds_reference,
      });
    }
    ecotoxicologyData.push(bird);
  }
  return ecotoxicologyData;
};

export const terrestresData = (environmentData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const terrestresDataArray = [];
  {
    // Le flag `is_dl50_bee` (Masquer la donnée) ne s'applique qu'à la
    // valeur+signe DL50, PAS au champ Commentaires sur les abeilles. Le
    // commentaire doit s'afficher dès qu'il est saisi.
    if (environmentData?.dl50_bee_comment) {
      const beesComment = {
        criteria: _t('mapdata.commentaires_abeilles', 'Commentaires sur les abeilles'),
        value: environmentData.dl50_bee_comment || '',
        references: [],
      };
      if (environmentData.dl50_bee_comment_reference) {
        beesComment.references.push({
          source: 'Référence',
          text: environmentData.dl50_bee_comment_reference,
        });
      }
      terrestresDataArray.push(beesComment);
    }

    const masked = !environmentData?.is_dl50_bee;
    const bees = {
      criteria: _t('mapdata.dl50_abeilles', 'Abeille DL<sub>50</sub> (cutanée/orale) (µg/abeille)'),
      value: masked ? '' : (environmentData.dl50_bee_value != null
        ? formatSignValue(environmentData.dl50_bee_sign, environmentData.dl50_bee_value)
        : ''),
      references: [],
    };
    if (masked) {
      bees.references.push({ text: '' });
    } else if (environmentData.dl50_bee_reference) {
      bees.references.push({
        source: 'Référence',
        text: environmentData.dl50_bee_reference,
      });
    }
    terrestresDataArray.push(bees);
  }

  {
    const masked = !environmentData?.is_cl50_worms;
    const worms = {
      criteria: _t('mapdata.cl50_vers_terre', 'Ver de terre CL<sub>50</sub> (mg/kg sol) - 14 j'),
      value: masked ? '' : (environmentData.cl50_worms_value != null
        ? formatSignValue(environmentData.cl50_worms_sign, environmentData.cl50_worms_value)
        : ''),
      references: [],
    };
    if (masked) {
      worms.references.push({ text: '' });
    } else if (environmentData.cl50_worms_reference) {
      worms.references.push({
        source: 'Référence',
        text: environmentData.cl50_worms_reference,
      });
    }
    terrestresDataArray.push(worms);
  }

  return terrestresDataArray;
};

export const aquaticData = (environmentData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const aquaticDataArray = [];

  if (environmentData?.aquatic_organisms) {
    const comment = {
      criteria: _t('mapdata.commentaires_organismes_aquatiques', 'Commentaires sur les organismes aquatiques'),
      value: environmentData?.aquatic_organisms || '',
      references: [],
    };
    if (environmentData.aquatic_organisms_reference) {
      comment.references.push({
        source: 'Référence',
        text: environmentData.aquatic_organisms_reference,
      });
    }
    aquaticDataArray.push(comment);
  }
  {
    const masked = !environmentData?.is_cl50_fishes;
    const fish = {
      criteria: _t('mapdata.truite_cl50', 'Truite arc-en-ciel CL<sub>50</sub> - 96 h (µg/L)'),
      value: masked ? '' : (environmentData.cl50_fishes_value != null
        ? formatSignValue(environmentData.cl50_fishes_sign, environmentData.cl50_fishes_value)
        : ''),
      references: [],
    };

    if (masked) {
      fish.references.push({ text: '' });
    } else if (environmentData.cl50_fishes_reference) {
      fish.references.push({
        source: 'Référence',
        text: environmentData.cl50_fishes_reference,
      });
    }

    aquaticDataArray.push(fish);
  }

  {
    const masked = !environmentData?.is_cl50_daphnias;
    const fish = {
      criteria: _t('mapdata.cl50_daphnies', 'Daphnie CL<sub>50</sub> / CE<sub>50</sub> - 48 h (µg/L)'),
      value: masked ? '' : (environmentData.cl50_daphnias_value != null
        ? formatSignValue(environmentData.cl50_daphnias_sign, environmentData.cl50_daphnias_value)
        : ''),
      references: [],
    };

    if (masked) {
      fish.references.push({ text: '' });
    } else if (environmentData.cl50_daphnias_reference) {
      fish.references.push({
        source: 'Référence',
        text: environmentData.cl50_daphnias_reference,
      });
    }

    aquaticDataArray.push(fish);
  }

  {
    const masked = !environmentData?.is_ce50_algae;
    const fish = {
      criteria: _t('mapdata.ce50_algues', 'Algue CE<sub>50</sub> (µg/L)'),
      value: masked ? '' : (environmentData.ce50_algae_value != null
        ? formatSignValue(environmentData.ce50_algae_sign, environmentData.ce50_algae_value)
        : ''),
      references: [],
    };

    if (masked) {
      fish.references.push({ text: '' });
    } else if (environmentData.ce50_algae_reference) {
      fish.references.push({
        source: 'Référence',
        text: environmentData.ce50_algae_reference,
      });
    }

    aquaticDataArray.push(fish);
  }

  {
    const masked = !environmentData?.is_ce50_vascular_plants;
    const fish = {
      criteria: _t('mapdata.ce50_plantes_vasculaires', 'Lenticule CE<sub>50</sub> (µg/L)'),
      value: masked ? '' : (environmentData.ce50_vascular_plants_value != null
        ? formatSignValue(environmentData.ce50_vascular_plants_sign, environmentData.ce50_vascular_plants_value)
        : ''),
      references: [],
    };

    if (masked) {
      fish.references.push({ text: '' });
    } else if (environmentData.ce50_vascular_plants_reference) {
      fish.references.push({
        source: 'Référence',
        text: environmentData.ce50_vascular_plants_reference,
      });
    }

    aquaticDataArray.push(fish);
  }
  return aquaticDataArray;
};

export const PhysiochimicPropertiesData = (environmentData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const physiochimicData = [];
  {
    const masked = !environmentData?.is_aqueous_solubility;
    const ph = {
      criteria: _t('mapdata.solubilite_aqueuse', 'Solubilité aqueuse (mg/L)'),
      value: masked ? '' : (environmentData.aqueous_solubility_value
        ? formatSignValue(environmentData.aqueous_solubility_sign, environmentData.aqueous_solubility_value)
        : ''),
      references: [],
    };

    if (masked) {
      ph.references.push({ text: '' });
    } else if (environmentData.aqueous_solubility_reference) {
      ph.references.push({
        source: 'Référence',
        text: environmentData.aqueous_solubility_reference,
      });
    }
    physiochimicData.push(ph);
  }
  {
    const masked = !environmentData?.is_absorption_coefficient;
    const ph = {
      criteria: _t('mapdata.coefficient_absorption', "Coefficient d'adsorption sur le carbone organique, Koc (mL/g)"),
      value: masked ? '' : (environmentData.absorption_coefficient_value
        ? formatSignValue(environmentData.absorption_coefficient_sign, environmentData.absorption_coefficient_value)
        : ''),
      references: [],
    };

    if (masked) {
      ph.references.push({ text: '' });
    } else if (environmentData.absorption_coefficient_reference) {
      ph.references.push({
        source: 'Référence',
        text: environmentData.absorption_coefficient_reference,
      });
    }
    physiochimicData.push(ph);
  }
  {
    const masked = !environmentData?.is_half_life_sol;
    const ph = {
      criteria: _t('mapdata.td50_demi_vie_sol', 'Demi-vie au sol, en condition aérobie, TD<sub>50</sub> (jour)'),
      value: masked ? '' : (environmentData.half_life_sol_value
        ? formatSignValue(environmentData.half_life_sol_sign, environmentData.half_life_sol_value)
        : ''),
      references: [],
    };

    if (masked) {
      ph.references.push({ text: '' });
    } else if (environmentData.half_life_sol_reference) {
      ph.references.push({
        source: 'Référence',
        text: environmentData.half_life_sol_reference,
      });
    }
    physiochimicData.push(ph);
  }

  {
    const masked = !environmentData?.is_half_life_water;
    const ph = {
      criteria: _t('mapdata.td50_demi_vie_eau', "Demi-vie dans l'eau, en condition aérobie, TD<sub>50</sub> (jour)"),
      value: masked ? '' : (environmentData.half_life_water_value
        ? formatSignValue(environmentData.half_life_water_sign, environmentData.half_life_water_value)
        : ''),
      references: [],
    };

    if (masked) {
      ph.references.push({ text: '' });
    } else if (environmentData.half_life_water_reference) {
      ph.references.push({
        source: 'Référence',
        text: environmentData.half_life_water_reference,
      });
    }
    physiochimicData.push(ph);
  }

  {
    const masked = !environmentData?.is_octanol_partition_coefficient;
    const ph = {
      criteria: _t('mapdata.coefficient_partage', 'Coefficient de partage octanol/eau (log P)'),
      value: masked ? '' : (environmentData.octanol_partition_coefficient_value
        ? formatSignValue(environmentData.octanol_partition_coefficient_sign, environmentData.octanol_partition_coefficient_value)
        : ''),
      references: [],
    };

    if (masked) {
      ph.references.push({ text: '' });
    } else if (environmentData.octanol_partition_coefficient_reference) {
      ph.references.push({
        source: 'Référence',
        text: environmentData.octanol_partition_coefficient_reference,
      });
    }
    physiochimicData.push(ph);
  }

  return physiochimicData;
};

export const behaviourInEnviromentData = (environmentData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);

  // R35(b) — always render both rows (Persistance ET Potentiel de lessivage)
  // with an empty value when the comment is absent, instead of dropping the row
  // (which made the section fall into its "Aucune donnée disponible" state).
  // NB: this mapper is shared with the product fiche (BehaviorInEnvironment.jsx).
  const persistance = {
    criteria: _t('mapdata.persistance', 'Persistance'),
    value: environmentData?.persistence_comment || '',
    references: [],
  };
  if (environmentData?.persistence_comment_reference) {
    persistance.references.push({
      source: 'Référence',
      text: environmentData.persistence_comment_reference,
    });
  }

  const lessivage = {
    criteria: _t('mapdata.potentiel_lessivage', 'Potentiel de lessivage'),
    value: environmentData?.mobility_comment || '',
    references: [],
  };
  if (environmentData?.mobility_comment_reference) {
    lessivage.references.push({
      source: 'Référence',
      text: environmentData.mobility_comment_reference,
    });
  }

  return [persistance, lessivage];
};

export const mapCultureRotationData = (cultureData) => {
  if (!cultureData || cultureData.length === 0) return [];

  const ALL_CULTURES_LABEL = 'Toutes les cultures non spécifiées';

  const filtered = cultureData
    .filter((item) => item?.culture_for_treatment_sheets || item?.deadline_for_treatment_sheets)
    .map((item) => ({
      criteria: item?.culture_for_treatment_sheets?.french_name || 'N/A',
      value: item?.deadline_for_treatment_sheets?.abbreviated_duration || 'N/A',
    }));

  // Separate "Toutes les cultures non spécifiées" items
  const specialItems = filtered.filter((item) => item.criteria === ALL_CULTURES_LABEL);
  const regularItems = filtered.filter((item) => item.criteria !== ALL_CULTURES_LABEL);

  // Sort regular items alphabetically by culture name (French locale)
  regularItems.sort((a, b) => a.criteria.localeCompare(b.criteria, 'fr'));

  // Style "Toutes les cultures non spécifiées" with bold black text
  const styledSpecialItems = specialItems.map((item) => ({
    ...item,
    criteriaStyle: { fontWeight: 'bold', color: '#000' },
  }));

  // Regular items first (alphabetically sorted), then special items at the end
  return [...regularItems, ...styledSpecialItems];
};

export const mapAgronomicInformationData = (product) => {
  const agronomicData = [];

  if (!isEmptyHtml(product?.incompatible_products)) {
    agronomicData.push({
      icon: produitNonCompatibleIcon,
      criteria: 'Produits non compatibles',
      value: product.incompatible_products,
    });
  }

  if (!isEmptyHtml(product?.ph_of_water)) {
    agronomicData.push({
      icon: pHIcon,
      criteria: "pH de l'eau",
      value: product.ph_of_water,
    });
  }

  if (!isEmptyHtml(product?.tank_mixes?.name)) {
    agronomicData.push({
      icon: produitNonCompatibleIcon,
      criteria: 'Mélanges en cuve',
      value: product.tank_mixes.name,
    });
  }

  if (!isEmptyHtml(product?.weather_restrictions)) {
    agronomicData.push({
      icon: cloudIcon,
      criteria: 'Restrictions météorologiques',
      value: product.weather_restrictions,
    });
  }

  if (!isEmptyHtml(product?.measures_to_counter_the_development_of_resistance)) {
    agronomicData.push({
      icon: developementsIcon,
      criteria: 'Mesures pour contrer le développement de la résistance',
      value: product.measures_to_counter_the_development_of_resistance,
    });
  }

  if (!isEmptyHtml(product?.afterglow)) {
    agronomicData.push({
      icon: remanenceIcon,
      criteria: 'Rémanence',
      value: product.afterglow,
    });
  }

  if (!isEmptyHtml(product?.particularities)) {
    agronomicData.push({
      criteria: 'Particularités',
      value: product.particularities,
    });
  }

  return agronomicData;
};

export const mapNoticeOnTicketData = (product) => {
  const noticeData = [];

  if (product?.poisoning_hazard_symbol_based_on_acute_toxicity) {
    noticeData.push({
      criteria: "Symbole de danger d'empoisonnement basé sur la toxicité aiguë",
      value: product?.poisoning_hazard_symbol_based_on_acute_toxicity?.label,
      hasSymbol: true,
      symbol: product?.poisoning_hazard_symbol_based_on_acute_toxicity,
    });
  }

  if (product?.additional_warning) {
    noticeData.push({
      criteria: 'Avertissements supplémentaires',
      value: formatArrayField(product?.additional_warning, 'name'),
    });
  }

  if (product?.allergens && product.allergens.length > 0) {
    noticeData.push({
      criteria: 'Allergènes',
      value: formatArrayField(product.allergens, 'name'),
    });
  }

  if (product?.maximum_exposure) {
    noticeData.push({
      criteria: 'Exposition maximale',
      value: product.maximum_exposure,
    });
  }

  return noticeData;
};

export const mapControlToolsData = (controlTools) => {
  if (!controlTools || controlTools.length === 0) return [];



  return controlTools.map((item) => ({
    // C72: without an image, the "Mot indicateur" (title) is RichText HTML —
    // render it, don't print the raw markup.
    criteria: item?.means_of_control?.images
      ? {
          type: 'image',
          src: item.means_of_control.images,
          alt: item.means_of_control.title,
        }
      : { type: 'html', html: item?.means_of_control?.title || 'N/A' },
    value: isEmptyHtml(item?.comment) ? 'N/A' : item?.comment,
    sageRecommendation: isEmptyHtml(
      item?.means_of_control?.sage_pesticide_recommendation
    )
      ? null
      : item?.means_of_control?.sage_pesticide_recommendation,
    hasImage: !!item?.means_of_control?.images,
  }));
};

const getEpiTypeLabel = (type) => {
  const typeLabels = {
    respiratory_tract: 'Protection des voies respiratoires',
    eyes: 'Protection des yeux',
    face: 'Protection du visage',
    hands: 'Protection des mains',
    body: 'Protection du corps',
    feet: 'Protection des pieds',
    head: 'Protection de la tête',
  };
  return typeLabels[type] || type;
};

export const mapEpiData = (product) => {
  const epiData = [];

  const epiFields = [
    'epi_head',
    'epi_respiratory_tract',
    'epi_eyes',
    'epi_body',
    'epi_hands',
    'epi_feet',
  ];

  epiFields.forEach((field) => {
    const epiItem = product?.[field];
    const commentField = `${field}_comment`;

    if (epiItem) {
      let commentValue = '';
      const commentData = product?.[commentField];

      if (commentData) {
        if (Array.isArray(commentData) && commentData.length > 0) {
          commentValue = commentData[0]?.comment || '';
        } else if (typeof commentData === 'object' && commentData !== null) {
          commentValue = commentData.comment || commentData.value || '';
        } else if (typeof commentData === 'string') {
          commentValue = commentData;
        }
      }

      const row = {
        type: getEpiTypeLabel(epiItem.type),
        minimum: epiItem.minimum_image
          ? {
              type: 'epiImage',
              src: epiItem.minimum_image,
              alt: epiItem.title,
              label: 'Minimum',
              comments: isEmptyHtml(epiItem.minimum_comments) ? null : epiItem.minimum_comments,
              sageRecommendation: isEmptyHtml(epiItem.minimum_sage_recommendation) ? null : epiItem.minimum_sage_recommendation,
            }
          : null,
        recommended: epiItem.recommended_image
          ? {
              type: 'epiImage',
              src: epiItem.recommended_image,
              alt: epiItem.title,
              label: 'Mieux',
              comments: isEmptyHtml(epiItem.recommended_comments) ? null : epiItem.recommended_comments,
              sageRecommendation: isEmptyHtml(epiItem.recommended_sage_recommendation) ? null : epiItem.recommended_sage_recommendation,
            }
          : null,
        optimal: epiItem.optional_image
          ? {
              type: 'epiImage',
              src: epiItem.optional_image,
              alt: epiItem.title,
              label: 'Idéal',
              comments: isEmptyHtml(epiItem.optional_comments) ? null : epiItem.optional_comments,
              sageRecommendation: isEmptyHtml(epiItem.optional_sage_recommendation) ? null : epiItem.optional_sage_recommendation,
            }
          : null,
        details: isEmptyHtml(commentValue) ? null : commentValue,
        additionalSageRecommendation: isEmptyHtml(
          epiItem.additional_sage_recommendation
        )
          ? null
          : epiItem.additional_sage_recommendation,
      };

      epiData.push(row);
    }
  });

  return epiData;
};

export const mapMoreEpiData = (product) => {
  const moreEpiData = [];

  // Global PPE maintenance, cleaning and order of removal data
  if (
    product?.global_ppe_maintenance_cleaning_and_order_of_removal?.length > 0
  ) {
    product.global_ppe_maintenance_cleaning_and_order_of_removal.forEach(
      (item) => {
        moreEpiData.push({
          criteria: 'Entretien, nettoyage et ordre de retrait des EPI',
          value: item.more_ppe || 'N/A',
          isHtml: true,
        });
      }
    );
  }

  // Global PPE storage data
  if (product?.global_ppe_storage?.length > 0) {
    product.global_ppe_storage.forEach((item) => {
      moreEpiData.push({
        criteria: 'Entreposage',
        value: item.more_ppe || 'N/A',
        isHtml: true,
      });
    });
  }

  // Global PPE miscellaneous data
  if (product?.global_ppe_miscellaneous?.length > 0) {
    product.global_ppe_miscellaneous.forEach((item) => {
      moreEpiData.push({
        criteria: 'Divers',
        value: item.more_ppe || 'N/A',
        isHtml: true,
      });
    });
  }

  return moreEpiData;
};

export const mapMitigationMeasuresData = (product) => {
  const mitigationData = [];

  if (product?.drift_mitigation?.name) {
    mitigationData.push({
      criteria: 'Atténuation de la dérive',
      value: product?.drift_mitigation?.name || 'N/A',
    });
  }

  if (product?.runoff_mitigation?.name) {
    mitigationData.push({
      criteria: 'Atténuation du ruissellement',
      value: product?.runoff_mitigation?.name || 'N/A',
    });
  }

  if (product?.pollinator_protection_label) {
    mitigationData.push({
      criteria: 'Protection des pollinisateurs',
      value: product.pollinator_protection_label?.name || 'N/A',
    });
  }

  return mitigationData;
};

// export const mapOtherMeasuresData = (product) => {
//   const otherMeasuresData = [];
//
//   if (product?.separation_distance_law) {
//     otherMeasuresData.push({
//       criteria:
//         "Distances d'éloignement à respecter lors de l'entreposage, de la préparation et de l'application de pesticides à des fins agricoles",
//       value: product.separation_distance_law?.name || 'N/A',
//     });
//   }
//
//   if (product?.pollinator_protection_law) {
//     otherMeasuresData.push({
//       criteria: 'Protection des pollinisateurs',
//       value: product.pollinator_protection_law?.name || 'N/A',
//     });
//   }
//
//   return otherMeasuresData;
// };

export const mapToxicityToNonTargetSpeciesData = (toxicityData) => {
  if (!toxicityData || toxicityData.length === 0) return [];

  return toxicityData
    .map((item) => ({
      criteria: item?.names_of_useful_species?.name || '-',
      toxicityLevel: {
        text: item?.toxicity_level?.name || '-',
        color: item?.toxicity_level?.color || '#000',
      },
      itemMentioned: item?.item_mentioned?.name || '-',
      comments: item?.comment || '-',
    }))
    .sort((a, b) => (a.criteria || '').localeCompare(b.criteria || '', 'fr'));
};

export const mapReferenceDosesData = (healthData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const referenceDosesData = [];

  // Dose de référence aiguë ARLA
  {
    const arlaData = {
      criteria: _t('mapdata.dose_ref_aigue_arla', 'Dose de référence aiguë ARLA (mg/kg/jour)'),
      value: healthData?.arla_acute_reference_dose || '',
      references: [],
    };

    if (healthData?.arla_acute_reference_dose_reference) {
      arlaData.references.push({
        text: healthData.arla_acute_reference_dose_reference,
      });
    }

    referenceDosesData.push(arlaData);
  }

  // Dose de référence aiguë EPA
  {
    const epaData = {
      criteria: _t('mapdata.dose_ref_aigue_epa', 'Dose de référence aiguë EPA (mg/kg/jour)'),
      value: healthData?.epa_acute_reference_dose || '',
      references: [],
    };

    if (healthData?.epa_acute_reference_dose_reference) {
      epaData.references.push({
        text: healthData.epa_acute_reference_dose_reference,
      });
    }

    referenceDosesData.push(epaData);
  }

  // Dose de référence aiguë OMS
  {
    const omsData = {
      criteria: _t('mapdata.dose_ref_aigue_oms', 'Dose de référence aiguë OMS (mg/kg/jour)'),
      value: healthData?.oms_acute_reference_dose || '',
      references: [],
    };

    if (healthData?.oms_acute_reference_dose_reference) {
      omsData.references.push({
        text: healthData.oms_acute_reference_dose_reference,
      });
    }

    referenceDosesData.push(omsData);
  }

  return referenceDosesData;
};

export const mapChronicReferenceDosesData = (healthData, t) => {
  const _t = (key, fallback) => (t ? t(`active-substance-details|${key}`, fallback) : fallback);
  const referenceDosesData = [];

  // Dose de référence chronique ARLA
  {
    const arlaData = {
      criteria: _t('mapdata.dose_ref_chronique_arla', 'Dose de référence chronique ARLA (mg/kg/jour)'),
      value: healthData?.arla_chronic_reference_dose || '',
      references: [],
    };

    if (healthData?.arla_chronic_reference_dose_reference) {
      arlaData.references.push({
        text: healthData.arla_chronic_reference_dose_reference,
      });
    }

    referenceDosesData.push(arlaData);
  }

  // Dose de référence chronique EPA
  {
    const epaData = {
      criteria: _t('mapdata.dose_ref_chronique_epa', 'Dose de référence chronique EPA (mg/kg/jour)'),
      value: healthData?.epa_chronic_reference_dose || '',
      references: [],
    };

    if (healthData?.epa_chronic_reference_dose_reference) {
      epaData.references.push({
        text: healthData.epa_chronic_reference_dose_reference,
      });
    }

    referenceDosesData.push(epaData);
  }

  // Dose de référence chronique OMS
  {
    const omsData = {
      criteria: _t('mapdata.dose_ref_chronique_oms', 'Dose de référence chronique OMS (mg/kg/jour)'),
      value: healthData?.oms_chronic_reference_dose || '',
      references: [],
    };

    if (healthData?.oms_chronic_reference_dose_reference) {
      omsData.references.push({
        text: healthData.oms_chronic_reference_dose_reference,
      });
    }

    referenceDosesData.push(omsData);
  }

  return referenceDosesData;
};
